import {
  Injectable,
  inject,
  signal,
  computed,
  effect,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import {
  Auth,
  SAMLAuthProvider,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendEmailVerification,
  user,
  User,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { HttpApiService } from '../api/http-api.service';
import { ApiError } from '../api/api-error';
import { ErrorLoggerService } from './error-logger.service';
import { UserTimezone } from '../time/user-timezone';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { Subject, filter, firstValueFrom, mergeWith } from 'rxjs';
import { extractRolesFromClaims } from '../auth/claims';
import { AppRole } from '../auth/roles';
import { resolveEnterpriseSsoProviderId } from '../auth/enterprise-sso';
import {
  ROLE_PERMISSIONS,
  assertValidPermission,
  AppPermission,
  resolveImpliedPermissions,
} from '../auth/permissions';
import { environment } from '../../../environments/environment';

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/invalid-email': 'Invalid email address',
  'auth/user-disabled': 'This account has been disabled',
  'auth/user-not-found': 'No account found with this email',
  'auth/wrong-password': 'Incorrect password',
  'auth/invalid-credential': 'Invalid email or password',
  'auth/too-many-requests': 'Too many attempts. Please try again later',
};

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

const SAML_ERROR_MESSAGES: Record<string, string> = {
  'auth/popup-closed-by-user': 'Sign-in was canceled.',
  'auth/popup-blocked': 'Popup was blocked. Please allow popups and try again.',
  'auth/unauthorized-domain': 'This domain is not authorized for sign-in.',
  'auth/operation-not-allowed': 'SAML provider is not enabled in Firebase Auth.',
};

interface UserProfile {
  uid: string;
  email: string;
  role: AppRole | null; // Primary/legacy role
  roles: AppRole[]; // All available roles
  permissions: string[]; // Resolved backend permissions
  permissionsSynced: boolean; // Whether backend permissions have been loaded
  constructionVendorId?: number | null;
  constructionVendorName?: string | null;
  acceptanceVendorId?: number | null;
  acceptanceVendorName?: string | null;
  timezone?: string | null;
  constructionVendorDisabled?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth = inject(Auth);
  private injector = inject(Injector);
  private httpApi = inject(HttpApiService);
  private errorLogger = inject(ErrorLoggerService);
  private userTimezone = inject(UserTimezone);
  private router = inject(Router);
  private runtimeConfig = environment;

  private user$ = user(this.auth);
  currentUser = toSignal(this.user$, { initialValue: null });

  loading = signal(false);
  error = signal<string | null>(null);
  userProfile = signal<UserProfile | null>(null);
  private userProfile$ = toObservable(this.userProfile, { injector: this.injector });
  private claimsSettled$ = new Subject<void>();
  private initialized = signal(false);
  private loggingIn = signal(false);
  private loggingOut = signal(false);
  private authStateReadyPromise: Promise<void>;

  isAuthenticated = computed(() => {
    this.initialized();
    return !this.loggingOut() && (this.currentUser() !== null || this.auth.currentUser !== null);
  });
  userRole = computed(() => this.userProfile()?.role || null);
  isInitialized = computed(() => this.initialized());
  userPermissions = computed(() => this.userProfile()?.permissions || []);
  constructionVendorName = computed(() => this.userProfile()?.constructionVendorName || null);
  acceptanceVendorName = computed(() => this.userProfile()?.acceptanceVendorName || null);
  permissionsSynced = computed(() => this.userProfile()?.permissionsSynced ?? false);

  constructor() {
    // Wait for Firebase to finish restoring auth state from persistence
    this.authStateReadyPromise = this.auth.authStateReady().then(() => {
      this.debugLog('Firebase Auth state ready');
      this.initialized.set(true);
    });

    // Restore userProfile when Firebase restores the session
    effect(() => {
      const user = this.currentUser();
      const isLoggingOut = this.loggingOut();
      const isLoggingIn = this.loggingIn();

      if (user && !this.userProfile() && !isLoggingOut && !isLoggingIn) {
        // User was restored from persistence, extract role
        this.debugLog('Restoring user profile from persisted session', user.email);
        this.setProfileFromUser(user).catch(async (err) => {
          console.error('❌ Failed to restore user profile:', err);
          this.error.set(
            err instanceof ApiError ? err.message : 'Session expired or account disabled.',
          );
          await this.logout();
          await this.router.navigate(['/login']);
        });
      }
    });
  }

  /**
   * Returns a promise that resolves when auth state is fully restored
   */
  async waitForAuthState(): Promise<void> {
    return this.authStateReadyPromise;
  }

  /**
   * Returns a promise that resolves as soon as cached session claims/profile are available,
   * without blocking router navigation on GET /api/auth/me/.
   */
  async waitForClaims(): Promise<void> {
    await this.waitForAuthState();

    if (!this.auth.currentUser || this.loggingOut()) {
      return;
    }

    const currentProfile = this.userProfile();
    if (currentProfile) {
      return;
    }

    await firstValueFrom(
      this.userProfile$.pipe(
        mergeWith(this.claimsSettled$, this.user$),
        filter(
          () =>
            this.userProfile() !== null ||
            this.loggingOut() ||
            !this.auth.currentUser,
        ),
      ),
    );
  }

  private handleAuthError(err: any, fallbackHandler: (code: string) => string): string {
    if (err instanceof ApiError) {
      return err.message;
    }
    if (err && typeof err === 'object' && 'message' in err && !err.code) {
      return err.message;
    }
    return fallbackHandler(err?.code);
  }

  private async executeLoginSequence(
    authFn: () => Promise<any>,
    errorHandler: (err: any) => string,
  ): Promise<boolean> {
    this.loading.set(true);
    this.error.set(null);
    this.loggingIn.set(true);

    let loginSuccess = false;
    try {
      const userCredential = await authFn();
      const authUser = userCredential.user;

      if (!authUser) {
        throw new Error('No user returned from authentication provider');
      }

      if (this.runtimeConfig.production && !authUser.emailVerified) {
        await runInInjectionContext(this.injector, () => sendEmailVerification(authUser));
        await runInInjectionContext(this.injector, () => signOut(this.auth));
        this.error.set('Email not verified. A verification email has been sent.');
        return false;
      }

      await this.waitForSignedInUser(authUser.uid);
      await this.setProfileFromUser(authUser);

      const profile = this.userProfile();
      if (profile) {
        this.errorLogger.logInfo('User signed in successfully', {
          event: 'successful_signin',
          email: profile.email,
        });
      }

      loginSuccess = true;
    } catch (err: any) {
      console.error('Login error:', err);
      await runInInjectionContext(this.injector, () => signOut(this.auth));
      this.userProfile.set(null);
      this.error.set(errorHandler(err));
    } finally {
      this.loading.set(false);
      this.loggingIn.set(false);
    }
    return loginSuccess;
  }

  async login(email: string, password: string): Promise<boolean> {
    return this.executeLoginSequence(
      () =>
        runInInjectionContext(this.injector, () =>
          signInWithEmailAndPassword(this.auth, email, password),
        ),
      (err: any) => this.handleAuthError(err, (code) => this.getErrorMessage(code)),
    );
  }

  async loginWithEnterpriseSso(): Promise<boolean> {
    const providerId = this.enterpriseSsoProviderId();
    if (!providerId) {
      this.error.set('Enterprise SSO is not configured for this environment.');
      return false;
    }

    const provider = new SAMLAuthProvider(providerId);
    return this.executeLoginSequence(
      () => runInInjectionContext(this.injector, () => signInWithPopup(this.auth, provider)),
      (err: any) => this.handleAuthError(err, (code) => this.getSamlErrorMessage(code)),
    );
  }

  async loginWithGoogle(): Promise<boolean> {
    const provider = new GoogleAuthProvider();

    const success = await this.executeLoginSequence(
      () => runInInjectionContext(this.injector, () => signInWithPopup(this.auth, provider)),
      (err: any) => this.handleAuthError(err, (code) => this.getErrorMessage(code)),
    );

    if (!success) return false;

    const profile = this.userProfile();
    if (profile && profile.roles.length === 0) {
      this.loading.set(true);
      let signupSuccess = false;
      try {
        const idToken = await this.auth.currentUser?.getIdToken();
        if (!idToken) throw new Error('Failed to get ID token');

        await firstValueFrom(this.httpApi.post('/api/auth/google-signup/', { id_token: idToken }));

        await this.auth.currentUser?.getIdToken(true);
        await this.setProfileFromUser(this.auth.currentUser!);
        signupSuccess = true;
      } catch (err: unknown) {
        this.error.set(this.extractGoogleSignupErrorMessage(err));
        await this.logout();
      } finally {
        this.loading.set(false);
      }
      return signupSuccess;
    }

    return true;
  }

  private extractGoogleSignupErrorMessage(err: unknown): string {
    if (err instanceof ApiError) {
      return err.message;
    }
    if (err && typeof err === 'object') {
      const apiErr = err as { error?: { error?: string }; message?: string };
      if (apiErr.error?.error) {
        return apiErr.error.error;
      }
    }
    return 'Failed to provision account. Are you invited?';
  }

  async logout(): Promise<void> {
    this.loggingOut.set(true);
    this.claimsSettled$.next();
    try {
      this.userProfile.set(null);
      this.userTimezone.setOverride(null);
      await runInInjectionContext(this.injector, () => signOut(this.auth));
      await firstValueFrom(this.user$.pipe(filter((currentUser) => currentUser === null)));
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      this.loggingOut.set(false);
    }
  }

  /**
   * Sends the user back to sign-in after the API rejects their token with a 401.
   * Pollers keep the shell mounted, so without this an expired session retries forever.
   * Later 401s from requests already in flight find the sign-out under way and do nothing.
   */
  async expireSession(): Promise<void> {
    if (this.loggingIn() || this.loggingOut() || !this.auth.currentUser) {
      return;
    }
    await this.logout();
    this.error.set(SESSION_EXPIRED_MESSAGE);
    await this.router.navigate(['/login']);
  }

  async updateTimezone(timezone: string | null): Promise<void> {
    const response = await firstValueFrom(
      this.httpApi.patch<{ timezone?: string | null }>('/api/auth/me/', { timezone }),
    );
    const resolvedTimezone = response.timezone !== undefined ? response.timezone : timezone;
    this.userTimezone.setOverride(resolvedTimezone);
    const currentProfile = this.userProfile();
    if (currentProfile) {
      this.userProfile.set({
        ...currentProfile,
        timezone: this.userTimezone.override(),
      });
    }
  }

  hasRole(role: AppRole): boolean {
    return this.userRole() === role;
  }

  hasPermission(permission: AppPermission | string): boolean {
    assertValidPermission(permission);
    return resolveImpliedPermissions(this.userPermissions()).has(permission);
  }

  async getIdToken(): Promise<string | null> {
    const currentUser = this.currentUser() ?? this.auth.currentUser;
    if (!currentUser) return null;
    return currentUser.getIdToken();
  }

  private async waitForSignedInUser(uid: string): Promise<void> {
    if (this.currentUser()?.uid === uid) {
      return;
    }

    await firstValueFrom(
      this.user$.pipe(
        filter((nextUser): nextUser is User => nextUser !== null && nextUser.uid === uid),
      ),
    );
  }

  private getErrorMessage(code: string): string {
    return AUTH_ERROR_MESSAGES[code] || 'An error occurred. Please try again';
  }

  isEnterpriseSsoConfigured(): boolean {
    return this.enterpriseSsoProviderId() !== null;
  }

  private enterpriseSsoProviderId(): string | null {
    return resolveEnterpriseSsoProviderId(this.runtimeConfig.firebase.enterpriseSso);
  }

  isGoogleConfigured(): boolean {
    return this.runtimeConfig.firebase.authMethods?.google ?? false;
  }

  isPasswordConfigured(): boolean {
    return this.runtimeConfig.firebase.authMethods?.password ?? true;
  }

  private async setProfileFromUser(user: User): Promise<void> {
    let idTokenResult = await user.getIdTokenResult();
    let roles = extractRolesFromClaims(idTokenResult.claims);

    if (roles.length === 0) {
      this.debugLog('Cached token has no roles. Forcing token refresh...');
      idTokenResult = await user.getIdTokenResult(true);
      roles = extractRolesFromClaims(idTokenResult.claims);
    }

    const nonAdminRole = roles.find((r) => r !== 'admin');
    const primaryRole = nonAdminRole || roles[0] || null;

    const defaultPermissionsSet = new Set<string>();
    for (const r of roles) {
      const perms = ROLE_PERMISSIONS[r];
      if (perms) {
        perms.forEach((p) => defaultPermissionsSet.add(p));
      }
    }
    if (primaryRole && ROLE_PERMISSIONS[primaryRole]) {
      ROLE_PERMISSIONS[primaryRole].forEach((p) => defaultPermissionsSet.add(p));
    }

    const tempProfile: UserProfile = {
      uid: user.uid,
      email: user.email || '',
      role: primaryRole,
      roles: roles,
      permissions: Array.from(defaultPermissionsSet),
      permissionsSynced: false,
    };

    this.userProfile.set(tempProfile);
    this.claimsSettled$.next();

    this.debugLog('User profile restored, fetching backend permissions...', {
      email: user.email,
      roles,
    });

    try {
      const backendProfile = await firstValueFrom(
        this.httpApi.get<{
          permissions: string[];
          construction_vendor_id?: number | null;
          construction_vendor_name?: string | null;
          acceptance_vendor_id?: number | null;
          acceptance_vendor_name?: string | null;
          timezone?: string | null;
        }>('/api/auth/me/'),
      );
      this.userTimezone.setOverride(backendProfile.timezone ?? null);
      this.userProfile.set({
        ...tempProfile,
        permissions: backendProfile.permissions,
        permissionsSynced: true,
        constructionVendorId: backendProfile.construction_vendor_id ?? null,
        constructionVendorName: backendProfile.construction_vendor_name ?? null,
        acceptanceVendorId: backendProfile.acceptance_vendor_id ?? null,
        acceptanceVendorName: backendProfile.acceptance_vendor_name ?? null,
        timezone: this.userTimezone.override(),
      });
      this.debugLog('Successfully synced backend permissions', backendProfile.permissions);
    } catch (err) {
      console.error('❌ Failed to sync backend permissions:', err);
      if (
        err instanceof ApiError &&
        err.status === 403 &&
        err.message === 'Construction Vendor access is temporarily disabled.'
      ) {
        this.userProfile.set({
          ...tempProfile,
          permissionsSynced: true,
          constructionVendorDisabled: true,
        });
        this.debugLog('Construction Vendor is disabled by backend');
        await this.router.navigate(['/access-pending']);
        return;
      }
      if (err instanceof ApiError && (err.status === 403 || err.status === 401)) {
        this.userProfile.set(null);
        throw err;
      }
      this.userProfile.set({
        ...tempProfile,
        permissionsSynced: true,
      });
    }
  }

  private getSamlErrorMessage(code: string): string {
    return SAML_ERROR_MESSAGES[code] || 'Unable to sign in with SAML. Please try again.';
  }

  private debugLog(message: string, payload?: unknown): void {
    if (this.runtimeConfig.production) {
      return;
    }
    if (payload === undefined) {
      console.log(message);
      return;
    }
    console.log(message, payload);
  }
}
